import { CampaignFormData, PixelStatus, PixelInfo, AdgroupFormData, AD_GROUP_WEBSITE_TYPES } from '@tt4b/ttam-enums';
import {
  useAllowlist,
  usePixelListApi,
  useStdObjectiveType,
  usePixelEventListApi,
  useRequest,
  useStore,
  useStdAdgroupIs,
  useCreativeActionTypeIs,
  useStdAdgroupCbo,
} from '@tt4b/ttam-hooks';
import { computed, Ref, ref, watch } from '@vue/composition-api';
import { i18n } from './../../../i18n';
import { createLogger } from '@tt4b/ttam-utils';
import { PixelItem, pixelOriginItem } from './types';
import PixelOnsiteImg from '@tt4b/ttam-assets/src/images/pixel-onsite.png';

type DataSourcePixelListOptions = {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<string>;
};

const logger = createLogger('PixelHooks');

const { state } = useStore();
const aadvid = computed(() => state.baseInfo.value.aadvid);

export const useCommonPixelUrl = (opt: { aadvid: string }) => `/i18n/events_manager/pixel?aadvid=${opt?.aadvid}`;

export const useCommonPixelEventUrl = (opt: { pixelCode: string; aadvid: string }) =>
  `/i18n/events_manager/pixel/detail/${opt?.pixelCode}?aadvid=${opt?.aadvid}`;

export const useCommonDomainUrl = (opt: { aadvid: string }) =>
  `/i18n/events_manager/pixel/domains?aadvid=${opt?.aadvid}`;

export const useCheckIsPixelDisabled = (opt: {
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<string>;
  copyNum: Ref<number>;
}) => {
  // FIX isCopy 状态是错误的，CreateMode 和 ActionType 不匹配
  const { isCreate, isEdit, isCopy } = useCreativeActionTypeIs({
    createMode: opt.createMode,
    adgroupFormData: opt.adgroupData,
  });

  const checkIsPixelDisabled = (i: PixelInfo) => {
    if (isCreate.value) {
      return i.availableQuota < 1;
    } else if (isEdit.value) {
      return i.id !== opt.adgroupData.value.adRefPixelId && i.availableQuota < 1;
    } else if (isCopy.value && i.availableQuota < opt.copyNum.value) {
      return true;
    }
    return false;
  };

  return {
    checkIsPixelDisabled,
  };
};

// pixel-list props
export function usePixelListDataSource(opt: DataSourcePixelListOptions) {
  const { isPcmPhase1White, isPcmPhase2White } = useAllowlist();
  const { isCreate, isEdit, isCopy } = useCreativeActionTypeIs({
    createMode: opt.createMode,
    adgroupFormData: opt.adgroupData,
  });
  const data = ref<PixelItem[]>([]);
  const isLoading = ref(false);
  const originalData = ref<PixelInfo[]>([]);
  const params = computed(() => ({
    promotion_website_type: opt.adgroupData.value.promotionWebsiteType,
    objective_type: opt.campaignData.value.objectiveType,
  }));
  const activateActiveTips = (hasPCM: boolean) =>
    (!isPcmPhase1White.value && !isPcmPhase2White.value) ||
    ((isPcmPhase1White.value || isPcmPhase2White.value) && hasPCM);

  const activateInactiveExtraTips = (haasPCM: boolean) =>
    (isPcmPhase1White.value || isPcmPhase2White.value) && !haasPCM;

  const isEmpty = computed(() => data.value.length === 0);
  const isDisabled = (i: PixelInfo) => {
    if (isCreate.value) {
      return i.availableQuota < 1;
    } else if (isEdit.value) {
      return i.id !== opt.adgroupData.value.adRefPixelId && i.availableQuota < 1;
    } else if (isCopy.value) {
      // TODO: 需要获取 copyNum
      return i.availableQuota < 1;
    }
    return false;
  };

  const run = async () => {
    try {
      isLoading.value = true;
      const res = await usePixelListApi(params.value);
      originalData.value = res;
      data.value = res
        .filter(i => i.name !== '')
        .map(i => {
          const isActive = i.status === PixelStatus.ACTIVE;
          const item: PixelItem = {
            status: isActive ? 'active' : 'inactive',
            disabled: isDisabled(i),
            showStatusPopover: isActive ? activateActiveTips(i.hasPcmConfig) : false,
            title: i.name,
            desc: `ID：${i.pixelCode}`,
            id: i.id,
            disabledUrl: `/i18n/perf/campaign?aadvid=${aadvid.value}`,
          };

          if (i.status === PixelStatus.NO_RECENT_ACTIVITY) {
            item.tipsConfig = [
              {
                desc: i18n.t('module_pixel_event_status_inactive_tooltip') as string,
                name: i18n.t('module_pixel_status_tooltip_link') as string,
                url: `${useCommonPixelEventUrl({
                  aadvid: aadvid.value,
                  pixelCode: i.pixelCode,
                })}&type=test_events`,
              },
            ];
            if (activateInactiveExtraTips(i.hasPcmConfig)) {
              item.tipsConfigExtra = {
                desc: i18n.t('module_pixel_wait_event_config') as string,
                name: i18n.t('common_go_to_web_event_configuration') as string,
                url: useCommonPixelEventUrl({
                  aadvid: aadvid.value,
                  pixelCode: i.pixelCode,
                }),
              };
            }
          }

          return item;
        });
      isLoading.value = false;
    } catch (error) {
      isLoading.value = false;
      logger.error(error);
    }
  };

  return {
    run,
    data,
    isLoading,
    isEmpty,
    originalData,
  };
}

// pixel-is-enable props
export function usePixelIsEnableVisible(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { isTraffic, isDpa } = useStdObjectiveType({ campaignFormData: opt.campaignData });
  const { isProductFromCatalog, isCLA } = useStdAdgroupIs({
    campaignFormData: opt.campaignData,
    adgroupFormData: opt.adgroupData,
  });
  const pixelIsEnableVisible = computed(() => {
    if (isCLA.value) {
      // CLA广告，如果开通了626白名单，则展示pixel开关允许可选，否则不展示开关必须使用pixel
      const { isCLAPixelOptionalWhite } = useAllowlist();
      return isCLAPixelOptionalWhite.value;
      // const { isCLAWhite } = useAllowlist();
      // return isCLAWhite.value;
    }
    return isTraffic.value || isDpa.value || isProductFromCatalog.value;
  });

  return {
    pixelIsEnableVisible,
  };
}

// pixel-event-list props
export function usePixelEventListDataSource(opt: DataSourcePixelListOptions & { pixelCode: Ref<string> }) {
  const { isEdit } = useCreativeActionTypeIs({
    createMode: opt.createMode,
    adgroupFormData: opt.adgroupData,
  });
  const params = computed(() => ({
    pixel_id: opt.adgroupData.value.adRefPixelId,
    promotion_website_type: opt.adgroupData.value.promotionWebsiteType,
    objective_type: opt.campaignData.value.objectiveType,
  }));
  const { data: originalData, isLoading, run, isEmpty } = useRequest(usePixelEventListApi, params, { manual: true });
  const { isPcmPhase1White, isPcmPhase2White } = useAllowlist();
  const showDomainList = (verifiedDomainLen: number) =>
    opt.adgroupData.value.promotionWebsiteType === AD_GROUP_WEBSITE_TYPES.offSite &&
    (isPcmPhase1White.value || isPcmPhase2White.value) &&
    verifiedDomainLen > 0;
  const pixelEventListFilterHander = pixelEvent => {
    const isNotEmptyName = pixelEvent.name !== '';
    if (isEdit.value) {
      return isNotEmptyName;
    }
    return isNotEmptyName && !pixelEvent.deprecated;
  };
  const data = computed(() =>
    originalData.value?.filter(pixelEventListFilterHander).map(i => {
      const isActive = i.status === PixelStatus.ACTIVE;
      const item: PixelItem = {
        status: isActive ? 'active' : 'inactive',
        showStatusPopover: isActive ? !isPcmPhase1White.value && !isPcmPhase2White.value : false,
        title: i.name,
        desc: '',
        id: i.id,
        tipsConfig: [],
        disabled: false,
        disabledUrl: '',
      };

      if (i.status === PixelStatus.NO_RECENT_ACTIVITY) {
        item.tipsConfig.push({
          desc: i18n.t('module_pixel_event_status_inactive_tooltip') as string,
          name: i18n.t('module_pixel_status_tooltip_link') as string,
          url: `${useCommonPixelEventUrl({
            aadvid: aadvid.value,
            pixelCode: opt.pixelCode.value,
          })}&type=test_events`,
        });
      }

      const len = i?.verifiedDomainList?.length ?? 0;
      if (showDomainList(len)) {
        item.tipsConfig.push({
          desc: i18n.t('module_pixel_event_verfied_domain') as string,
          name:
            len === 1
              ? `${i?.verifiedDomainList?.[0]}`
              : `${len}\n${i18n.t('module_show_more_domain', { number: len })}`,
          url: useCommonDomainUrl({ aadvid: aadvid.value }),
        });
      } else if (isPcmPhase2White.value) {
        item.tipsConfig.push({
          desc: i18n.t('module_pixel_event_wait_verify_domain_content') as string,
          name: i18n.t('common_go_to_web_event_configuration') as string,
          url: useCommonPixelEventUrl({
            aadvid: aadvid.value,
            pixelCode: opt.pixelCode.value,
          }),
        });
      }

      return item;
    }),
  );
  const originData = computed(() => {
    if (originalData.value) {
      return [...originalData.value];
    } else {
      return [];
    }
  });

  watch(
    params,
    (newVal, oldVal) => {
      // 没有 pixel 不请求
      const isNoPixel = newVal.promotion_website_type === AD_GROUP_WEBSITE_TYPES.offSite && !newVal.pixel_id;
      if (isNoPixel) {
        return;
      }
      run();
    },
    {
      deep: true,
      immediate: true,
    },
  );

  return {
    run,
    data,
    isEmpty,
    originData,
    isLoading,
  };
}

// pixel-website-type props
export function usePixelWebsiteTypePropsVisible(opt: { campaignData: Ref<CampaignFormData> }) {
  const { isConversion } = useStdObjectiveType({ campaignFormData: opt.campaignData });
  const visible = computed(() => isConversion.value);

  return {
    visible,
  };
}

export function usePixelWebsiteTypeConfigs() {
  const offSiteConfig = ref({
    label: AD_GROUP_WEBSITE_TYPES.offSite,
    labelName: i18n.t('module_adgroup_owned_website'),
    tooltip: {
      text: i18n.t('module_adgroup_owned_website_hint'),
      showKey: 'create_adgroup_external_website_hover',
    },
  });

  const onSiteConfig = ref({
    label: AD_GROUP_WEBSITE_TYPES.onSite,
    labelName: i18n.t('module_adgroup_tiktok_onsite_page'),
    tooltip: {
      text: i18n.t('module_adgroup_tiktok_onsite_page_hint'),
      url: '/help/article?aid=10007478',
      urlText: i18n.t('common_learn_more'),
      img: PixelOnsiteImg,
      showKey: 'create_adgroup_promotion_ip_hover',
    },
  });

  return {
    offSiteConfig,
    onSiteConfig,
  };
}

// pixel-ios-tips props
export function usePixelIosTipsVisible(opt: { adgroupData: Ref<AdgroupFormData>; pixelList: Ref<PixelInfo[]> }) {
  const { adgroupData, pixelList } = opt;
  const { isPcmPhase1White, isPcmPhase2White } = useAllowlist();
  const visible = computed(
    () =>
      adgroupData.value.adRefPixelId &&
      !adgroupData.value.promotionWebsiteType &&
      pixelList.value.every(i => !i.hasPcmConfig) &&
      (isPcmPhase1White.value || isPcmPhase2White.value),
  );

  return {
    visible,
  };
}

// pixel-mid-funnel-tips props
export function usePixelMidFunnelTipsVisible(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const isShowShopifyMidFunnelWarn = ref(false);
  const isSelectedCompletePayment = computed(() => adgroupData.value.externalAction === 96);

  const visible = computed(() => isShowShopifyMidFunnelWarn.value && isSelectedCompletePayment.value);

  return {
    visible,
  };
}

// native-tips props
export function usePixelNativeTipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { isTiktokWebPage } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const externalAction = computed(() => adgroupData.value.externalAction);

  const visible = computed(() => isTiktokWebPage.value && externalAction.value);

  return {
    visible,
  };
}

// pixel-event-inactive-tips props
export function usePixelEventInActiveTipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  pixelEventList: Ref<pixelOriginItem[]>;
}) {
  const { adgroupData, pixelEventList } = opt;
  const externalAction = computed(() => adgroupData.value.externalAction);
  // 组件计算属性
  // pixel标准化的事件，会打标deprecated属性，新增广告只能使用非deprecated的事件，存量广告使用全量数据
  const pixelStandEventList = computed(() => pixelEventList.value.filter(item => !item.deprecated));
  const visible = computed(() => {
    const selectedEvent = pixelStandEventList.value?.find(event => `${event.id}` === `${externalAction.value}`);
    return selectedEvent && selectedEvent.status === PixelStatus.NO_RECENT_ACTIVITY;
  });

  return {
    visible,
  };
}

export function usePropsSelectedPixel() {
  const selectedPixel = computed(() => (state.pixelInfo.data.value ? [state.pixelInfo.data.value] : []));

  return {
    selectedPixel,
  };
}

export function usePropsPixelSwitchDisabled(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  disablePixel: Ref<boolean>;
}) {
  const { adgroupData, campaignData, disablePixel } = opt;
  const { isStandardAdsWithCatalog } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isProductSales } = useStdObjectiveType({ campaignFormData: campaignData });
  const productSalesPixelSwitchDisabled = computed(
    () => isProductSales.value && isStandardAdsWithCatalog.value && isCBO.value,
  );
  const disabledPixelSwitch = computed(() => disablePixel.value || productSalesPixelSwitchDisabled.value);

  return {
    disabledPixelSwitch,
  };
}
